import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, LifeBuoy, Search } from "lucide-react";
import { getRole } from "../../auth/session";
import { type HelpArticle, type Role, helpGroups } from "../../help/types";
import { getArticle, searchArticles, visibleArticles } from "../../help/content";
import { HelpArticleView } from "../../help/HelpArticleView";

function ArticleCard({ article }: { article: HelpArticle }) {
  const Icon = article.icon;
  return (
    <Link
      to={`/help/${article.id}`}
      className="flex items-start gap-3 rounded-xl border border-riot-navy-100 bg-white p-4 transition-colors hover:border-riot-gold-300 hover:bg-riot-gold-50/40 dark:border-riot-navy-700 dark:bg-riot-navy-900 dark:hover:border-riot-gold-500/40 dark:hover:bg-riot-navy-800"
    >
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-riot-navy-50 text-riot-gold-500 dark:bg-riot-navy-800">
        <Icon size={18} />
      </span>
      <span>
        <span className="block text-sm font-semibold text-riot-navy-900 dark:text-white">{article.title}</span>
        <span className="mt-0.5 block text-xs leading-relaxed text-riot-navy-500 dark:text-riot-navy-400">{article.summary}</span>
      </span>
    </Link>
  );
}

export function HelpCenterPage() {
  const { articleId } = useParams();
  const role = (getRole() ?? "viewer") as Role;
  const [query, setQuery] = useState("");

  const visible = useMemo(() => visibleArticles(role), [role]);
  const results = useMemo(() => (query.trim() ? searchArticles(query, role) : null), [query, role]);
  const selected = articleId ? getArticle(articleId) : undefined;
  const selectedVisible = selected && visible.some((a) => a.id === selected.id);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-5 flex items-center gap-2.5">
        <LifeBuoy size={22} className="text-riot-gold-500" />
        <h1 className="text-xl font-bold text-riot-navy-900 dark:text-white">Help &amp; Guides</h1>
      </div>

      <div className="relative mb-6">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-riot-navy-400" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the guides — e.g. 'roll out firmware', 'invite a user', 'device offline'"
          aria-label="Search help guides"
          className="w-full rounded-lg border border-riot-navy-200 bg-white py-2.5 pl-9 pr-3 text-sm text-riot-navy-900 placeholder:text-riot-navy-400 focus:border-riot-gold-400 focus:outline-none focus:ring-1 focus:ring-riot-gold-400 dark:border-riot-navy-700 dark:bg-riot-navy-900 dark:text-white"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[15rem_1fr]">
        {/* Left nav */}
        <nav className="hidden lg:block">
          <div className="space-y-4">
            {helpGroups.map((group) => {
              const items = (results ?? visible).filter((a) => a.group === group.id);
              if (items.length === 0) return null;
              return (
                <div key={group.id}>
                  <p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-riot-navy-400">{group.label}</p>
                  {items.map((a) => (
                    <Link
                      key={a.id}
                      to={`/help/${a.id}`}
                      className={`block rounded-md px-2 py-1.5 text-[13px] transition-colors ${
                        a.id === articleId
                          ? "bg-riot-gold-50 font-medium text-riot-navy-900 dark:bg-riot-navy-700 dark:text-riot-gold-400"
                          : "text-riot-navy-600 hover:bg-riot-navy-50 hover:text-riot-navy-900 dark:text-riot-navy-300 dark:hover:bg-riot-navy-800 dark:hover:text-white"
                      }`}
                    >
                      {a.title}
                    </Link>
                  ))}
                </div>
              );
            })}
          </div>
        </nav>

        {/* Main pane. A live search wins over an open article so the search box
            never looks dead on a detail page; otherwise show the article, else the index. */}
        <div>
          {results ? (
            <div>
              <p className="mb-3 text-sm text-riot-navy-500 dark:text-riot-navy-400">
                {results.length === 0
                  ? `No guides match "${query}". Try a different word.`
                  : `${results.length} guide${results.length === 1 ? "" : "s"} for "${query}"`}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {results.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          ) : selected && selectedVisible ? (
            <article>
              <Link to="/help" className="mb-3 inline-flex items-center gap-1 text-xs text-riot-navy-500 hover:text-riot-navy-800 dark:text-riot-navy-400 dark:hover:text-white">
                <ArrowLeft size={13} /> All guides
              </Link>
              <div className="mb-4 flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-riot-navy-50 text-riot-gold-500 dark:bg-riot-navy-800">
                  <selected.icon size={22} />
                </span>
                <div>
                  <h2 className="text-lg font-bold text-riot-navy-900 dark:text-white">{selected.title}</h2>
                  <p className="text-sm text-riot-navy-500 dark:text-riot-navy-400">{selected.summary}</p>
                </div>
              </div>
              <HelpArticleView article={selected} currentRole={role} />
            </article>
          ) : (
            <div className="space-y-6">
              {articleId && !selectedVisible && (
                <p className="rounded-lg border border-riot-gold-200 bg-riot-gold-50 px-3.5 py-2.5 text-sm text-riot-gold-800 dark:border-riot-gold-500/20 dark:bg-riot-gold-500/10 dark:text-riot-gold-300">
                  That guide isn't available for your role. Here's everything you can access.
                </p>
              )}
              {helpGroups.map((group) => {
                const items = visible.filter((a) => a.group === group.id);
                if (items.length === 0) return null;
                return (
                  <section key={group.id}>
                    <div className="mb-2">
                      <h2 className="text-sm font-semibold text-riot-navy-900 dark:text-white">{group.label}</h2>
                      <p className="text-xs text-riot-navy-500 dark:text-riot-navy-400">{group.blurb}</p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {items.map((a) => (
                        <ArticleCard key={a.id} article={a} />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
